import { ActionReducerMapBuilder, createSlice, PayloadAction } from '@reduxjs/toolkit'
import {
  AdditionalSLAParamsType,
  AdditionalSLAResponseType,
  GetReasonsSLAResponseType,
} from 'features/Order/services/additionalSLA'
import {
  fetchAdditionalSLAList,
  fetchAllActiveHubs,
  fetchOrderLocations,
  fetchReasonsAdditionalSLA,
  submitAdditionalSLA,
  turnOffAdditionalSLA,
} from './thunk'

type LocationType = {
  id?: number
  name?: string
  location_id?: number
  location_name?: string
  location_type?: string
  disabled?: boolean
}[]

export type InitialStateType = {
  isLoading: boolean
  additionalSLAParams: AdditionalSLAParamsType
  additionalSLAList: AdditionalSLAResponseType[`data`]
  showPopUp: boolean
  showPopUpDeactivate: 'CLOSED' | 'SINGLE' | 'MULTIPLE'
  reasonsList: GetReasonsSLAResponseType[`data`]
  deactivateSLAIds: number[]
  deactivateSLAIdSingle: number[] // yes, eventhough it will be only send one id, it's still an array
  locations: LocationType
  isExtending: boolean
  selectedData: Nullable<AdditionalSLAResponseType[`data`][number]>
  pagination: {
    has_next: boolean
    has_prev: boolean
    limit: number
    prev_cursor: number
    next_cursor: number
  }
}

const initialState: InitialStateType = {
  isLoading: false,
  additionalSLAParams: {},
  additionalSLAList: [],
  showPopUp: false,
  showPopUpDeactivate: 'CLOSED',
  reasonsList: [],
  deactivateSLAIds: [],
  deactivateSLAIdSingle: [],
  locations: [],
  isExtending: false,
  selectedData: null,
  pagination: {
    has_next: true,
    has_prev: false,
    limit: 20,
    prev_cursor: 0,
    next_cursor: 0,
  },
}

const additionalSLASlice = createSlice({
  name: 'additionalSLA',
  initialState,
  reducers: {
    setAdditionalSLAParams: (
      state,
      action: PayloadAction<InitialStateType['additionalSLAParams']>,
    ) => {
      state.additionalSLAParams = action.payload
    },
    setAdditionalSLAList: (state, action: PayloadAction<InitialStateType['additionalSLAList']>) => {
      state.additionalSLAList = action.payload
    },
    setLoading: (state, action: PayloadAction<boolean>) => {
      state.isLoading = action.payload
    },
    setShowPopUp: (state, action: PayloadAction<boolean>) => {
      state.showPopUp = action.payload
    },
    setReasonsList: (state, action: PayloadAction<GetReasonsSLAResponseType[`data`]>) => {
      state.reasonsList = action.payload
    },
    setShowPopUpDeactivate: (
      state,
      action: PayloadAction<InitialStateType['showPopUpDeactivate']>,
    ) => {
      state.showPopUpDeactivate = action.payload
    },
    setDeactivateSLAIds: (state, action: PayloadAction<number[]>) => {
      state.deactivateSLAIds = action.payload
    },
    setDeactivateSLAIdSingle: (state, action: PayloadAction<number>) => {
      if (action.payload === 0) {
        state.deactivateSLAIdSingle = []
        return
      }
      state.deactivateSLAIdSingle = [action.payload]
    },
    setIsExtending: (state, action: PayloadAction<boolean>) => {
      state.isExtending = action.payload
    },
    setSelectedData: (state, action: PayloadAction<InitialStateType['selectedData']>) => {
      state.selectedData = action.payload
    },
  },
  extraReducers: (builder: ActionReducerMapBuilder<InitialStateType>) => {
    builder.addCase(fetchAllActiveHubs.fulfilled, (state, action) => {
      state.locations = [...state.locations].map((location) => {
        // Check if the location is in action.payload.data
        const isActive = action.payload.data.some((active) => active.id === location.location_id)
        return {
          ...location,
          disabled: isActive ? true : location.disabled, // Set disabled to true if active
        }
      })
    })
    builder.addCase(fetchOrderLocations.fulfilled, (state, action) => {
      state.locations = action.payload.data
        .filter((location) => location.location_type?.toLowerCase() === 'hub')
        .map((location) => ({ ...location, disabled: false }))
    })
    builder.addCase(turnOffAdditionalSLA.fulfilled, (state) => {
      state.showPopUpDeactivate = 'CLOSED'
    })
    builder.addCase(turnOffAdditionalSLA.rejected, (state) => {
      state.showPopUpDeactivate = 'CLOSED'
    })
    builder.addCase(submitAdditionalSLA.fulfilled, (state) => {
      state.showPopUp = false
    })
    builder.addCase(submitAdditionalSLA.rejected, (state) => {
      state.showPopUp = false
    })
    builder.addCase(fetchAdditionalSLAList.fulfilled, (state, action) => {
      state.additionalSLAList = action.payload.data
      state.pagination = action.payload.pagination
    })
    builder.addCase(fetchReasonsAdditionalSLA.fulfilled, (state, action) => {
      state.reasonsList = action.payload.data
    })
  },
})

export const {
  setAdditionalSLAParams,
  setAdditionalSLAList,
  setLoading,
  setShowPopUp,
  setReasonsList,
  setShowPopUpDeactivate,
  setDeactivateSLAIdSingle,
  setDeactivateSLAIds,
  setIsExtending,
  setSelectedData,
} = additionalSLASlice.actions
export default additionalSLASlice.reducer
